import room_user, { buyProperty, users } from "./room_user";
import { gamestates, roundstates } from "./enums";
import message from "./message";
import { getMoveTurns, getRoundRevenue, properties, relatives, requestRiverPropertyDamage, requestSeaPropertyDamage, getUpgradeMatrix, propertyPartIsUnderwater, cowShedIsDamaged } from "./property";
import playerState, { isLivingWithRelatives, getRepairCost } from "./playerstate";

export const NoPlayer = "NA";
export const skipFinalDiceRoll = true;
export const maxRounds = 8;

//How many turns before nature based protection is full
export const natureBasedProtectionLevel = 1;

export enum GameCommandTypes {
  NA,
  RequestVoteOnStopBank,
  RequestVoteOnNatureBased,
  VoteOnStopBank,
  VoteOnNatureBased,
}

export class GameCommand {

  CommandType: GameCommandTypes;
  UUID: string;
  Value: any;

  constructor(type: GameCommandTypes, uuid: string, value: any) {
    this.CommandType = type;
    this.UUID = uuid;
    this.Value = value;
  }

}


export default class Game {

  id: string;
  host: string;
  timestamp: number;
  gameUniqueId: string;

  //These fields are controlled by the HOST
  gamestate: number;
  governmentSubsidy: boolean;
  riverStates: number[] = [];
  stormStates: number[] = [];

  //  Each player manages rounds locally
  chat: { [id: string]: message } | null = null;

  idToPlayerUUID: { [id: string]: string };

  networkPlayers: { [id: string]: boolean } = {};
  playerStates: { [id: string]: playerState } = {};

  //Commands from client to host
  gameCommands: { [id: string]: GameCommand } = {};

  constructor(id: string, host: string) {

    this.id = id;
    this.governmentSubsidy = Math.random() < 0.5;
    this.gameUniqueId = crypto.randomUUID();
    this.host = host;
    this.timestamp = Date.now();
    this.gamestate = gamestates.PlayerSelect;

    this.idToPlayerUUID = {
      "0": NoPlayer,
      "1": NoPlayer,
      "2": NoPlayer,
      "3": NoPlayer,
      "4": NoPlayer,
    };

    //First stage is always no event (since the dice roll for the next round happens at the end of the round)
    this.riverStates.push(riverDamageLevel.none);
    this.stormStates.push(stormDamageLevel.none);

    for (let i = 0; i < maxRounds - 1; i++) {
      this.riverStates.push(rollRiverDice(this.riverStates.length + 1));
      this.stormStates.push(rollStormDice());
    }

    //Push an extra one that's just the no event
    this.riverStates.push(riverDamageLevel.none);
    this.stormStates.push(stormDamageLevel.level1);

  }

}

export enum riverDamageLevel {
  none = 0,
  highFlood = 1,
  severeFlood = 2,
}

export enum stormDamageLevel {
  none = 0, //Never gets rolled but here for first and last rounds
  level1 = 1,
  level2 = 2,
  cyclone = 4,
}

//Could possibly rework this function and the next into a single function to simplify
export function getRoundOrders(game: Game, round: number): string {

  const result: string[] = [];
  const players = Object.keys(game.playerStates);

  for (let i = 0; i < players.length; i++) {
    result.push(users[parseInt(players[round % players.length])].analyticsId);
    round++;
  }

  return result.join(",");

}

export function currentPlayer(game: Game): number {

  let round = -1;

  if (game.playerStates) {
    for (const player of Object.values(game.playerStates)) {

      //Are we up to the next round
      let thisPlayerRound = player.roundState < roundstates.RoundReport ? player.round : player.round + 1;

      if (round == -1) {
        round = thisPlayerRound;
      }
      else {
        round = Math.min(thisPlayerRound, round);
      }
    }
  }

  //There were no players this round
  if (round == -1) {
    round = 0;
  }

  //To start with, we calculate who should be the starting round player for this round
  let startingRoundPlayer = -1;
  for (let i = 0; i <= round; i++) {
    startingRoundPlayer = wrapAroundPlayer(game, startingRoundPlayer);
  }

  let playerToSearch = startingRoundPlayer;
  let lastPlayer = playerToSearch;

  do {

    if (!game || !game.playerStates || !game.playerStates[playerToSearch]) {
      return -1;
    }

    const player = game.playerStates[playerToSearch];
    let thisPlayerRound = player.roundState < roundstates.RoundReport ? player.round : player.round + 1;

    //This player is on the round, AND they haven't finished their turn yet
    if (player.roundState < roundstates.RoundReport || thisPlayerRound <= round) {
      return playerToSearch;
    }

    lastPlayer = playerToSearch;
    playerToSearch = wrapAroundPlayer(game, playerToSearch);

  }
  //Repeat until we've looped all of the way around to the start
  while (playerToSearch != startingRoundPlayer)

  //If we're on the report view, just treat the last player as the current one
  return lastPlayer;
}

export function wrapAroundPlayer(game: Game, startingNumber: number): number {
  do {
    startingNumber += 1;
    if (startingNumber >= Object.keys(game.idToPlayerUUID).length) {
      startingNumber = 0;
    }
  }
  while (game.idToPlayerUUID[startingNumber] == NoPlayer)
  return startingNumber;
}

//Progresses the game, but only returns the part of the class it needs to update
export function progressGame(game: Game, uuid: string, offline: boolean) {

  const playerState = game.playerStates[getUserId(game, uuid)];

  //First pass, we simply go to the next state
  switch (playerState.roundState) {

    case roundstates.Storm:
      playerState.roundState = roundstates.Repair;
      break;

    case roundstates.Repair:
      playerState.roundState = roundstates.Calendar;
      break;

    case roundstates.Calendar:
      playerState.roundState = roundstates.ChooseOption;
      break;

    case roundstates.ChooseOption:

      //Are we part way through growing the nature based solution?
      if (playerState.isMakingNatureBasedSolution
        && playerState.natureBasedState < 2) {
        playerState.natureBasedState++;
      }


      //At the end of the choose round, we're updating anything that's moving that hasn't been moved this turn
      for (const property of Object.values(playerState.properties)) {
        const propertyData = properties[property.property];

        for (let i = 0; i < property.propertyPartStates.length; i++) {

          //If the part is moving
          if (property.propertyPartStates[i].isMoving
            //If the part hasn't fully moved to where it's meant to move to
            && property.propertyPartStates[i].movePosition < getMoveTurns(propertyData.property_parts[i])) {

            property.propertyPartStates[i].movePosition++;

          }
        }
      }

      playerState.roundState = roundstates.RoundReport;

      //If we're offline, skip the round report
      if (offline) {
        //        progressGame(game, uuid, offline);
      }

      break;

    case roundstates.RoundReport:
      playerState.roundState = roundstates.RollTheDice;

      //We're on the last round, so skip the dice roll     
      if (skipFinalDiceRoll && playerState.round + 1 >= maxRounds) {
        progressGame(game, uuid, offline);
      }
      break;

    case roundstates.RollTheDice:

      //We go to the next round
      playerState.round++;
      playerState.roundState = roundstates.EndOfRound;
      break;
  }

  //We need to wait until EVERY player has rolled
  prepareRound(game, uuid, offline);

}

//We need to verify that EVERY player is at least at the target state
export function allPlayersAtState(game: Game, round: number, state: number): boolean {

  if (!game.playerStates) {
    return false;
  }

  let countedPlayers = 0;

  for (const keyPair of Object.entries(game.idToPlayerUUID)) {
    if (keyPair[1] != NoPlayer) //This is a used player
    {
      const playerState = game.playerStates[keyPair[0]];
      if (!playerState) {
        continue;
      }

      //This player is in the past
      if (playerState.round < round) {
        return false;
      }

      //This player is in this round, but hasn't caught up yet
      if (playerState.round == round
        && playerState.roundState < state) {
        return false;
      }

      countedPlayers++;
    }
  }

  return countedPlayers > 0;
}

export function rollRiverDice(round: number): number {

  const rollAmount = Math.floor(Math.random() * 12);
  const severeSides = round >= 4 ? 4 : 3;
  const highSides = 4;

  if (rollAmount < severeSides) {
    return riverDamageLevel.severeFlood;
  }
  else if (rollAmount < severeSides + highSides) {
    return riverDamageLevel.highFlood;
  }
  else {
    return riverDamageLevel.none;
  }

}

export function rollStormDice(): number {

  const rollAmount = Math.floor(Math.random() * 12);

  const level1Sides = 6;
  const level2Sides = 5;

  if (rollAmount < level1Sides) {
    return stormDamageLevel.level1;
  }
  else if (rollAmount < level1Sides + level2Sides) {
    return stormDamageLevel.level2;
  }
  else {
    return stormDamageLevel.cyclone;
  }

}

export function riverStateCheat(game: Game, round: number, value: number): void {
  game.riverStates[round + 1] = value; //Sets for the following round
}

export function stormStateCheat(game: Game, round: number, value: number): void {
  game.stormStates[round + 1] = value; //Sets for the following round
}

export function getRiverStage(game: Game, playerState: playerState): number {
  let round = playerState.round;
  if (playerState.roundState >= roundstates.RollTheDice) {   //Use the next round's result instead
    round++;
  }
  return game.riverStates[round];
}

export function getSurgeStage(game: Game, playerState: playerState): number {
  let round = playerState.round;
  if (playerState.roundState >= roundstates.RollTheDice) {   //Use the next round's result instead
    round++;
  }
  return game.stormStates[round];
}

export function getTotalStormLevel(game: Game, playerState: playerState): number {

  let stormLevel = getSurgeStage(game, playerState);

  //Prevent storm level from reaching into the "safe zone"
  //Subtract 1 to match with guide
  let totalStormLevel = Math.min(playerState.round + stormLevel - 1, maxRounds);

  //Handling for nature based solution
  if (playerState.natureBasedSeaSolution) {

    totalStormLevel = 0; //Nature based solution blocks the storm completely

  } else if (playerState.seaWallHeight) {

    //Seawall partially blocks storm surge
    if (totalStormLevel > playerState.seaWallRound) {
      totalStormLevel = Math.max(playerState.seaWallRound, totalStormLevel - playerState.seaWallHeight * 2);
    }

  }

  //DO NOT let the storm surge go past the 
  return Math.min(totalStormLevel, maxRounds);

}

export function needsToMoveOutOfSea(playerState: playerState, nextRound: boolean): boolean {

  for (const propertyState of playerState.properties) {
    let property = properties[propertyState.property];

    const isLivedIn = property.key == playerState.livedInProperty;
    if (!isLivedIn) {//only care about the lived in proeprty
      continue;
    }

    //We need to find if ANY property part requires a sell if underwater
    for (let i = 0; i < propertyState.propertyPartStates.length; i++) {
      const propertyPart = property.property_parts[i];
      const matrix = getUpgradeMatrix(propertyPart);

      //We can't get the details about this property part, OR we don't care if it's under water, continue
      if (!matrix || matrix.MustMoveIfUnderwater != "yes") {
        continue;
      }

      const propertyPartState = propertyState.propertyPartStates[i];

      if (propertyPartIsUnderwater(playerState, propertyPart, propertyPartState, nextRound)) {

        //This current property part is under water, so we must move in with relatives
        return true;

      }

    }

  }

  return false;
}

function forcedMove(playerState: playerState): boolean {

  //Set whether a move was forced because of a destroyed cowshed
  playerState.cowshedDestroyed = (cowShedIsDamaged(playerState)
    && playerState.isOverdue
    && playerState.bankBalance < getRepairCost(playerState));

  const needsToMove = needsToMoveOutOfSea(playerState, false) || playerState.cowshedDestroyed;

  //force a move if we're not already living with relatives, and we're forced to move out of the sea
  if (isLivingWithRelatives(playerState) == false && needsToMove) {
    buyProperty(playerState, relatives, playerState.livedInProperty, true);
    return true;
  }

  return false;

}

export function prepareRound(game: Game, uuid: string, offline: boolean) {

  const playerState = game.playerStates[getUserId(game, uuid)];

  //Second pass - do we want to automatically skip any particular round?
  switch (playerState.roundState) {

    case roundstates.Storm:

      //Skip on the first round
      if (playerState.round == 0) {
        progressGame(game, uuid, offline);
      }
      break;

    case roundstates.Repair:

      let anyDamage = false;

      //Is the seawall destroyed
      if (playerState.seaWallHeight
        && playerState.round > playerState.seaWallRound + (playerState.seaWallHeight * 2)) {
        playerState.seaWallDestroyedHeight = playerState.seaWallHeight;
        playerState.seaWallHeight = 0;
        anyDamage = true;
      }
      else {
        playerState.seaWallDestroyedHeight = 0;
      }

      //We test moving out of sea AFTER destroying the seawall
      if (forcedMove(playerState)) {
        anyDamage = true;
      }

      //calculate storm surge damage
      const stormLevel = getSurgeStage(game, playerState);
      const isCyclone = stormLevel >= 4;

      if (isCyclone && playerState.natureBasedSeaSolution) {
        playerState.natureBasedSeaSolution = false; //Destroy the nature based sea solution in the result of a cyclone
        playerState.natureBasedSeaDestroyed = true;
        anyDamage = true;
      }
      else {
        playerState.natureBasedSeaDestroyed = false;
      }

      //High or severe
      const isSevereRiverFlood = getRiverStage(game, playerState) == riverDamageLevel.severeFlood;

      if (isSevereRiverFlood && playerState.stopBank) {
        playerState.stopBankDestroyed = true; //The stop bank is only destroyed if we actually have one
        playerState.stopBank = false;
        anyDamage = true;
      }
      else {
        playerState.stopBankDestroyed = false; //The stop bank is never destroyed in high or less
      }

      for (const propertyState of playerState.properties) {

        let property = properties[propertyState.property];

        //We don't apply damage to a property that's already been damaged
        if (propertyState.damageLevel > riverDamageLevel.none) {
          anyDamage = true;
          continue;
        }

        for (let i = 0; i < propertyState.propertyPartStates.length; i++) {
          const propertyPart = property.property_parts[i];
          const propertyPartState = propertyState.propertyPartStates[i];

          //We can apply river damage
          let thisRepairCost = requestRiverPropertyDamage(
            game,
            playerState,
            propertyPart,
            propertyPartState); //Fully moved     

          if (thisRepairCost) {
            //we were damaged by the river
            propertyState.damageLevel = isSevereRiverFlood ? riverDamageLevel.severeFlood : riverDamageLevel.highFlood;
          }

          //We can apply SS damage
          const thisSeaRepairCost = requestSeaPropertyDamage(game, playerState, propertyPart, propertyPartState);
          if (thisSeaRepairCost > thisRepairCost) {
            //Override the river cost with the sea cost
            thisRepairCost = thisSeaRepairCost;
            propertyState.damageLevel = riverDamageLevel.severeFlood;
          }

          //Doesn't need to be repaired
          if (thisRepairCost == 0) {
            continue;
          }
          else {
            anyDamage = true;
          }

          propertyPartState.repairCost = thisRepairCost;

        }
      }


      //We want to skip repair if we've got nothing to repair AND there's no damage this round anyway
      if (playerState.round == 0 || anyDamage == false) {
        progressGame(game, uuid, offline);
      }
      break;

    case roundstates.Calendar:

      playerState.display_repair = 0;

      //Try to repair if we can
      if (playerHasAnyDamage(playerState)) {
        const myRepairCost = getRepairCost(playerState);
        if (playerState.bankBalance >= myRepairCost) {

          playerState.display_repair = myRepairCost

          //Do the repair
          playerState.bankBalance -= myRepairCost;
          for (const property of playerState.properties) {
            property.damageLevel = riverDamageLevel.none;
            for (const part of property.propertyPartStates) {
              part.repairCost = 0;
            }
          }

          playerState.isOverdue = false;
        }
        else {
          //Repair is now overdue
          playerState.isOverdue = true;
        }
      }

      let totalIncome = playerState.display_income = playerState.display_rent = 0;

      for (const propertyState of playerState.properties) {

        const property = properties[propertyState.property];
        const isLivedIn = property.key == playerState.livedInProperty;

        let thisRevenue = getRoundRevenue(
          game,
          property,
          playerState);

        totalIncome += thisRevenue;
        //If we live here, we increase the display income
        if (isLivedIn) {
          playerState.display_income += thisRevenue;
        }
        else {
          playerState.display_rent += thisRevenue;
        }

      }

      playerState.bankBalance += totalIncome;

      //Always skip the first calendar
      if (playerState.round == 0) {
        progressGame(game, uuid, offline);
      }
      break;

  }

}

function playerHasAnyDamage(playerState: playerState): boolean {

  for (const property of playerState.properties) {
    if (property.damageLevel > riverDamageLevel.none) {
      return true;
    }
  }

  return false;
}


export function getBankBalance(game: Game, playerNumber: number): number {

  const playerState = game.playerStates[playerNumber];
  return playerState.bankBalance;

}

export function isHost(game: Game, uuid: string, offline: boolean) {

  //If this is an offline game, the player is the host by default
  if (offline) {
    return true;
  }

  return game.host == uuid;
}

export function getUnassignedPlayers(game: Game): number {

  let result = 0;
  for (const player of Object.keys(game.networkPlayers)) {
    if (getUserId(game, player) == NoPlayer) {
      result = + 1;
    }
  }
  return result;

}

export function getUser(game: Game, uuid: string): room_user {
  return users[parseInt(getUserId(game, uuid), 10)];
}

export function getUserId(game: Game, uuid: string): string {

  for (const key of Object.keys(game.idToPlayerUUID)) {
    if (game.idToPlayerUUID[key] == uuid) {
      return key;
    }
  }

  return NoPlayer;
}

export enum gameType {
  townshipFlood = 0,
  futureCostsAotearoa = 1,
}

export function seaZone(state: playerState, includeMangroves: boolean, seaWallHeight = -1, nextRound = false): number {

  //If seawall height is undefined, use the game's seawall height
  if (seaWallHeight < 0) {
    seaWallHeight = state.seaWallHeight;
  }

  if (seaWallHeight > 0) {
    //The sea extends up to the seawall but no farther
    return Math.min(state.round, seaWallZone(state));
  }

  let result = state.round;
  if (nextRound) {
    result += 1;
  }

  if (includeMangroves && state.natureBasedSeaSolution) {
    result += 1;
  }

  return result;

}

export function seaWallZone(state: playerState): number {

  if (state.seaWallHeight) {
    return state.seaWallRound;
  }
  return Math.max(1, state.round);

}

function parseDbPath(GameType: gameType, path: string): string {
  if (GameType == gameType.futureCostsAotearoa) {
    return "FCA/" + path;
  }
  return path;
}

export function gamePath(GameType: gameType, gameId: string): string {
  return parseDbPath(GameType, "/games/" + gameId + "/");
}

//Needs both the game and the translation function
export function repairChat(game: Game, T: (key: string) => string): message[] {

  if (!game.chat) {
    game.chat = {};
  }

  let result: message[] = [];

  for (const msg of Object.values(game.chat)) {

    //Find the matching username + avatar
    for (const playerKey of Object.keys(game.idToPlayerUUID)) {
      if (game.idToPlayerUUID[playerKey] == msg.senderId) {

        const player = users[parseInt(playerKey, 10)];
        const realName = T(player.realname);
        msg.senderId = msg.username = realName;
        msg.avatar = "avatars/avatar" + realName + ".png";

        result.push(msg);
        break;
      }
    }


  }

  return result;

}

/*
export function playerIsSafe(playerState: playerState): boolean {

  for (const propertyState of playerState.properties) {

    let property = properties[propertyState.property];

    for (let i = 0; i < propertyState.propertyPartStates.length; i++) {
      const propertyPart = property.property_parts[i];
      //      const propertyPartState = propertyState.propertyPartStates[i];
      //    const fullyProtectedByNatureBased = playerState.natureBasedState >= natureBasedProtectionLevel;

      //We are ONLY safe if EVERYONE has moved to a safe house
      const thisRepairCost = requestPropertyDamage(
        propertyPart.damageClass,
        true,
        false,
        false,
        false,
        false); //Fully moved

      //      const thisRepairCost = requestPropertyDamage(
      //      propertyPart.damageClass,
      //    true,
      //  propertyPartState.movePosition >= getMoveTurns(propertyPart),
      // propertyPartState.hasRaisedFloor,
      // fullyProtectedByNatureBased); //Fully moved        

      if (thisRepairCost > 0) {
        return false;
      }


    }
  }

  return true;

}
*/
